import { useEffect, useRef } from "react";
import type { LocaleCode } from "../localization/types";
import { notificationLocaleService, type NotificationLocaleService } from "../services/notificationLocaleService";

export function useNotificationLocaleViewModel(
  userId: string | null,
  locale: LocaleCode,
  service: NotificationLocaleService = notificationLocaleService,
) {
  // Serialize client attempts across locale/account changes. A timed-out callable
  // can still finish later; the service's server revision fences that stale write.
  const pending = useRef<Promise<void>>(Promise.resolve());
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let running = false;
    let failures = 0;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const synchronize = () => {
      if (cancelled || running) return;
      clearTimeout(retry);
      running = true;
      pending.current = pending.current.then(async () => {
        if (cancelled) return;
        try {
          await service.save(userId, locale);
          failures = 0;
        } catch {
          if (!cancelled) {
            console.warn("Notification language could not be saved; it will retry on reconnect or app focus.");
            if (++failures <= 2) retry = setTimeout(synchronize, failures * 1000);
          }
        } finally {
          running = false;
        }
      });
    };
    const onVisible = () => { if (document.visibilityState === "visible") synchronize(); };
    synchronize();
    window.addEventListener("online", synchronize);
    window.addEventListener("focus", synchronize);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearTimeout(retry);
      window.removeEventListener("online", synchronize);
      window.removeEventListener("focus", synchronize);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [userId, locale, service]);
}
